package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentResponse
import ai.jessie.shared.documents.DocumentValidationResponse
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch
import org.w3c.files.File

enum class UploadPhase {
    IDLE,
    VALIDATING,
    UPLOADING,
}

// A rejected selection; `existingDocumentId` links a visible duplicate (A3) and nothing else.
data class DocumentProblem(val message: String, val existingDocumentId: String? = null)

// Step 5: the selected file and the server's validation of it, nothing stored (B3).
data class PendingDocument(val file: File, val validation: DocumentValidationResponse)

data class DocumentUiState(
    val documents: List<DocumentResponse>? = null,
    val loading: Boolean = false,
    val error: OnboardingProblem? = null,
    val phase: UploadPhase = UploadPhase.IDLE,
    val progress: Int? = null,
    val pending: PendingDocument? = null,
    val uploadProblem: DocumentProblem? = null,
    val uploaded: DocumentResponse? = null,
    val notice: String? = null,
)

data class DocumentActions(
    val refresh: () -> Unit,
    val select: (File?) -> Unit,
    val confirm: () -> Unit,
    val cancel: () -> Unit,
)

/**
 * UC-017 inbox and upload of one organisation, and the UC-020 list of one workflow state (B8).
 * Loading is single-flight; a new selection replaces an unfinished validation (latest selection
 * wins); upload is single-flight and cannot be cancelled once sent. The page scope is keyed by
 * organisation, document and state, so a disposed scope never publishes another tenant's answer.
 * Visibility is the server's (B4); this only presents it. The opened document is
 * [DocumentWorkflowState]'s.
 */
class DocumentState(
    private val port: DocumentPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
    private val filter: DocumentFilter,
) {
    var state by mutableStateOf(DocumentUiState())
        private set

    private var validation: Job? = null

    val actions =
        DocumentActions(
            refresh = ::refresh,
            select = ::select,
            confirm = ::confirm,
            cancel = ::cancel,
        )

    fun refresh() {
        if (state.loading) return
        state = state.copy(loading = true, error = null)
        scope.launch {
            try {
                val documents =
                    if (filter == DocumentFilter.INBOX) port.inbox(organisationId)
                    else port.list(organisationId, filter)
                currentCoroutineContext().ensureActive()
                state = state.copy(documents = documents)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: DocumentRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = cleared(failure.loadProblem())
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state = state.copy(error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(loading = false)
            }
        }
    }

    // UC-006: a session or organisation loss found while processing the opened document.
    fun lost(problem: OnboardingProblem) {
        state = cleared(problem)
    }

    // Steps 3-5. A1 sizes are refused before any transfer; the server checks everything again.
    private fun select(file: File?) {
        if (state.phase == UploadPhase.UPLOADING) return
        validation?.cancel()
        validation = null
        val base =
            state.copy(
                phase = UploadPhase.IDLE,
                progress = null,
                pending = null,
                uploadProblem = null,
                uploaded = null,
                notice = null,
            )
        val size = file?.size?.toDouble()
        state =
            when {
                file == null -> base
                size == null || size < 1 || size > MAX_DOCUMENT_BYTES ->
                    base.copy(uploadProblem = sizeProblem())
                else -> base.copy(phase = UploadPhase.VALIDATING, progress = 0)
            }
        if (file == null || state.phase != UploadPhase.VALIDATING) return
        validation = scope.launch {
            transfer(confirming = false) {
                val validated =
                    port.validate(organisationId, file) { state = state.copy(progress = it) }
                currentCoroutineContext().ensureActive()
                state = state.copy(pending = PendingDocument(file, validated))
            }
        }
    }

    // Steps 6-9: the server reconfirms role and organisation and stores atomically.
    private fun confirm() {
        val pending = state.pending ?: return
        if (state.phase != UploadPhase.IDLE) return
        state = state.copy(phase = UploadPhase.UPLOADING, progress = 0, uploadProblem = null)
        scope.launch {
            transfer(confirming = true) {
                val document =
                    port.upload(organisationId, pending.file) { state = state.copy(progress = it) }
                currentCoroutineContext().ensureActive()
                state =
                    state.copy(
                        pending = null,
                        uploaded = document,
                        // A new document is in the Inbox only (UC-017 step 9).
                        documents =
                            if (filter != DocumentFilter.INBOX) state.documents
                            else
                                listOf(document) +
                                    state.documents.orEmpty().filter {
                                        it.documentId != document.documentId
                                    },
                        notice =
                            "„${document.fileName}“ wurde hochgeladen und ist jetzt in der Inbox.",
                    )
            }
        }
    }

    // A5: the browser drops the selection; nothing was stored, so nothing is sent.
    private fun cancel() {
        if (state.phase == UploadPhase.UPLOADING) return
        if (state.pending == null && state.phase != UploadPhase.VALIDATING) return
        validation?.cancel()
        validation = null
        state =
            state.copy(
                phase = UploadPhase.IDLE,
                progress = null,
                pending = null,
                uploadProblem = null,
                notice = "Upload abgebrochen. Es wurde kein Dokument hinzugefügt.",
            )
    }

    private suspend fun transfer(confirming: Boolean, block: suspend () -> Unit) {
        try {
            block()
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: DocumentRequestFailure) {
            currentCoroutineContext().ensureActive()
            rejected(failure, confirming)
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            // An answer that is not the expected one is never presented as stored (A6).
            state =
                state.copy(
                    pending = null,
                    uploadProblem =
                        if (confirming) notUploadedProblem()
                        else DocumentProblem(unavailableProblem().message),
                )
        } finally {
            currentCoroutineContext().ensureActive()
            state = state.copy(phase = UploadPhase.IDLE, progress = null)
        }
    }

    private fun rejected(failure: DocumentRequestFailure, confirming: Boolean) {
        state =
            when {
                // The session or organisation itself is gone: the page-level UC-006 outcome.
                failure.status == HTTP_UNAUTHENTICATED ||
                    failure.detail.code == "ORGANISATION_REQUIRED" -> cleared(failure.loadProblem())
                // A4: no inbox stays.
                failure.deniesAccess() ->
                    state.copy(
                        documents = null,
                        pending = null,
                        uploadProblem = failure.uploadProblem(confirming),
                    )
                else ->
                    state.copy(pending = null, uploadProblem = failure.uploadProblem(confirming))
            }
    }

    // A cleared page also drops a running validation, so its late answer cannot reappear.
    private fun cleared(problem: OnboardingProblem): DocumentUiState {
        validation?.cancel()
        validation = null
        return DocumentUiState(loading = state.loading, error = problem)
    }
}

private const val HTTP_UNAUTHENTICATED = 401
